import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { motion } from 'framer-motion';

const LabelLanding = () => {
    const [artists, setArtists] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                // In production, we'd pull from `artists` and `projects`.
                // For now, we simulate fetching for the UI layout.
                const projSnap = await getDocs(query(collection(db, 'projects'), orderBy('createdAt', 'desc')));
                const p = [];
                projSnap.forEach(doc => p.push({ id: doc.id, ...doc.data() }));
                setProjects(p);

                // Mock Artist Data
                setArtists([
                    { id: '1', name: 'Mood RN', role: 'Artist / Producer', img: 'https://images.unsplash.com/photo-1493225457224-eda0e6fdc463?w=800&q=80' },
                    { id: '2', name: 'Tejati', role: 'Vocalist', img: 'https://images.unsplash.com/photo-1516280440502-65f532ba7170?w=800&q=80' }
                ]);
            } catch (err) {
                console.error("Error fetching portfolio:", err);
            }
            setLoading(false);
        };
        fetchContent();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center text-white">
                 <div className="w-10 h-10 border-4 border-white/10 border-t-white rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white selection:bg-white/30 font-['Montserrat'] overflow-x-hidden">
            
            {/* HERO */}
            <section className="relative min-h-[90vh] flex flex-col items-center justify-center text-center px-4 overflow-hidden">
                <img 
                    src="/hero_bg.png" 
                    alt="Background" 
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-[linear-gradient(to_bottom,black_0%,transparent_20%,transparent_50%,black_100%)] z-10" />
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                    className="z-20"
                >
                    <h1 className="text-6xl md:text-8xl font-black uppercase tracking-tighter mb-4 font-['Montserrat']" style={{ 
                        textShadow: '0 1px 0 #ccc, 0 2px 0 #c9c9c9, 0 3px 0 #bbb, 0 4px 0 #b9b9b9, 0 5px 0 #aaa, 0 6px 1px rgba(0,0,0,.1), 0 0 5px rgba(0,0,0,.1), 0 1px 3px rgba(0,0,0,.3), 0 3px 5px rgba(0,0,0,.2), 0 5px 10px rgba(0,0,0,.25), 0 10px 10px rgba(0,0,0,.2), 0 20px 20px rgba(0,0,0,.15)' 
                    }}>
                        MôØD
                    </h1>
                    <p className="text-sm md:text-md uppercase tracking-[0.4em] font-bold text-zinc-400 mb-12 max-w-xl mx-auto leading-relaxed">
                        An Architected Reality. The label redefining interactive sound architecture.
                    </p>
                    <a 
                        href="https://moodstream.web.app/subscribe" 
                        target="_blank" rel="noreferrer"
                        className="px-8 py-4 bg-white text-black font-black uppercase tracking-widest text-xs rounded-full hover:scale-105 transition-transform"
                    >
                        Enter The App
                    </a>
                </motion.div>
            </section>

            {/* ARTISTS */}
            <section className="py-24 px-8 max-w-7xl mx-auto">
                <h2 className="text-2xl font-black uppercase tracking-widest mb-12 border-b border-white/10 pb-4">Our Roster</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {artists.map(artist => (
                        <div key={artist.id} className="group relative overflow-hidden rounded-2xl aspect-[3/4] cursor-pointer">
                            <img src={artist.img} alt={artist.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-100" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                            <div className="absolute bottom-0 left-0 p-8">
                                <h3 className="text-2xl font-black uppercase tracking-tighter">{artist.name}</h3>
                                <p className="text-xs text-zinc-400 uppercase tracking-widest font-bold mt-2">{artist.role}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* DISCOGRAPHY */}
            <section className="py-24 px-8 max-w-7xl mx-auto border-t border-white/5">
                <h2 className="text-2xl font-black uppercase tracking-widest mb-12 border-b border-white/10 pb-4">Master Ledger</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {projects.slice(0, 8).map(track => (
                        <div key={track.id} className="group">
                            <div className="aspect-square bg-zinc-900 rounded-xl overflow-hidden mb-4 relative">
                                {track.artwork ? (
                                    <img src={track.artwork} alt={track.title} className="w-full h-full object-cover grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center border border-white/10 text-white/20">Empty</div>
                                )}
                            </div>
                            <h4 className="font-bold text-sm truncate">{track.title}</h4>
                            <p className="text-xs text-zinc-500 truncate">{track.artist}</p>
                        </div>
                    ))}
                </div>
            </section>

        </div>
    );
};

export default LabelLanding;
